import impressionModel from './models/impressionModel';
import clickModel from './models/clickModel';
import detailImpressionModel from './models/detailImpressionModel';
import addToCartModel from './models/addToCartModel';
import removeFromCartModel from './models/removeFromCartModel';
import { beginCheckoutModel, addPaymentInfoModel, addShippingInfoModel } from './models/checkoutModel';
import purchaseModel from './models/purchaseModel';
import checkoutOptionModel from './models/checkoutOptionModel';
import toggleAddOnModel from './models/toggleAddOnModel';


class Analytics {
  constructor(options = {}) {
    this.dataLayer = window.dataLayer;
    this.productImpressions = options.productImpressions || this.productImpressionsDefault;
    this.productClick = options.productClick || this.productClickDefault.bind(this);
    this.productDetailImpression = options.productDetailImpression ||
    this.productDetailImpressionDefault;
    this.addToCart = options.addToCart || this.addToCartDefault;
    this.removeFromCart = options.removeFromCart || this.removeFromCartDefault;
    this.gaEnabled = this.gaEnabled.bind(this);
    this.pushToDataLayer = this.pushToDataLayer.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
  }

  productImpressionsDefault(products, list) {
    this.pushToDataLayer({ ecommerce: null });
    this.pushToDataLayer(
      impressionModel(products, list),
    );
  }

  productClickDefault(product, list, options) {
    if (!this.gaEnabled()) {
      return window.location.assign(product.productUrl);
    }

    this.pushToDataLayer({ ecommerce: null });
    return this.pushToDataLayer(
      clickModel(product, list, options),
    );
  }

  productDetailImpressionDefault(product) {
    this.pushToDataLayer({ ecommerce: null });
    this.pushToDataLayer(
      detailImpressionModel(product),
    );
  }

  addToCartDefault(product, quantity, eventCallback) {
    if (!this.gaEnabled()) {
      return eventCallback();
    }

    this.pushToDataLayer({ ecommerce: null });
    return this.pushToDataLayer(
      addToCartModel(product, quantity, eventCallback),
    );
  }

  toggleAddOn(type, selected, page, eventCallback) {
    if (!this.gaEnabled()) {
      return eventCallback();
    }

    return this.pushToDataLayer(
      toggleAddOnModel(type, selected, page, eventCallback),
    );
  }

  removeFromCartDefault(product, quantity, eventCallback) {
    if (!this.gaEnabled()) {
      return eventCallback();
    }

    this.pushToDataLayer({ ecommerce: null });
    return this.pushToDataLayer(
      removeFromCartModel(product, quantity, eventCallback),
    );
  }

  /*
  * This event is not present in the GA4 event list
  * This will be kept as it is for UA compatibility
  * refered from https://developers.google.com/analytics/devguides/migration/ecommerce/gtagjs-compatibility#ua-ga4-parameters
  */
  addCheckoutOption(step, option) {
    if (!this.gaEnabled()) {
      return;
    }

    this.pushToDataLayer(
      checkoutOptionModel(step, option),
    );
  }

  checkoutProcess(order, step, totals) {
    if (!order) {
      return false;
    }

    this.pushToDataLayer({ ecommerce: null });

    switch (step) {
      case 'shipping': {
        return this.pushToDataLayer(
          addShippingInfoModel(order, totals),
        );
      }
      case 'payment': {
        return this.pushToDataLayer(
          addPaymentInfoModel(order, totals),
        );
      }
      case 'begin_checkout': {
        return this.pushToDataLayer(
          beginCheckoutModel(order, totals),
        );
      }
      default: {
        return null;
      }
    }
  }

  purchase(order) {
    if (!order) {
      return false;
    }

    this.pushToDataLayer({ ecommerce: null });
    return this.pushToDataLayer(
      purchaseModel(order),
    );
  }

  trackEvent(customEvent) {
    if (customEvent) {
      return this.pushToDataLayer({ event: 'trackEvent', ...customEvent });
    }

    return false;
  }

  pushToDataLayer(ecommMeasurement) {
    let throttledEventMeasurement;

    if (ecommMeasurement.eventCallback) {
      let callbackCalled = false;

      throttledEventMeasurement = {
        ...ecommMeasurement,
        eventCallback: () => {
          if (callbackCalled) {
            return;
          }

          callbackCalled = true;
          ecommMeasurement.eventCallback();
        },
      };
    } else {
      throttledEventMeasurement = ecommMeasurement;
    }

    let timer = 0;
    const timerCeiling = 3000;
    const interval = 250;
    const hasGoogleAnalytics = setInterval(() => {
      timer += interval;

      if (this.gaEnabled()) {
        this.dataLayer.push(throttledEventMeasurement);
        clearInterval(hasGoogleAnalytics);
      }

      if (timer >= timerCeiling) {
        clearInterval(hasGoogleAnalytics);
      }
    }, interval);
  }

  gaEnabled() {
    this.dataLayer = window.dataLayer || [];

    const gtmStartedEvent = this.dataLayer.find(element => element['gtm.start']);

    if (gtmStartedEvent && gtmStartedEvent['gtm.uniqueEventId']) {
      return true;
    }

    return false;
  }
}

export default Analytics;
